import React, {useEffect, useState} from 'react';

import {Add as AddIcon} from '@mui/icons-material';
import {
    Fab,
    List,
    Stack,
    Typography
} from '@mui/material';

import {
    ConfirmDialog,
    Drawer,
    MenuHeader,
    Text, useConfirmDialog
} from 'components';
import {unTranslated, useTranslation} from 'hooks';
import {MenuItem} from 'module/client/index';
import {useLicenseManager} from 'module/user';
import {useUserUtils} from 'module/user/useUser';
import {useLayoutStore, useSnackbarStore} from 'store';

import Create from './Create';
import Edit from './Edit';
import {IconButton} from './index';
import {useApiDelete, useApiQuery} from './useClientApi';

const styles = {
    container: {
        paddingX: 2,
    },
    actionButtonContainer: {
        paddingY: 2.5,
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: 'grey.darkest2',
        cursor: 'pointer',
    },
    actionButton: {
        flexShrink: 0,
        backgroundColor: 'red.highlight',
        marginRight: 2,
        ':hover': {
            backgroundColor: 'red.dark',
        },
    },
    listHolder: {
        padding: 0,
    },
};

const Client = ({client, onClick, onToggleMenu, showMenu, onDelete, onEdit}) => {
    const translate = useTranslation();

    const {isOwner} = useUserUtils();

    const collapseButtons = [];

    if (isOwner(client.user_id)) {
        collapseButtons.push(<IconButton
            key="pencil"
            icon="pencil"
            onClick={() => onEdit(client)}
        />);
        collapseButtons.push(<IconButton
            key="delete"
            icon="delete"
            color="red.dark"
            onClick={() => onDelete(client)}
        />);
    }

    return <MenuItem
        onClick={() => onClick(client)}
        avatarText={client.name}
        image={client.image}
        listItemTextPrimary={client.name}
        listItemTextSecondary={`${client.teams.length} ${translate('team.teams')}, ${client.members.length} ${translate('client.members')}`}
        pullerFunction={(e) => onToggleMenu(e, client)}
        collapseButtons={collapseButtons}
        showMenu={showMenu}
    />;
};

const Menu = () => {
    const translate = useTranslation();
    const {hasAccess} = useLicenseManager();
    const {isOwner} = useUserUtils();

    const [selection, setSelection] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const setShowClient = useLayoutStore(state => state.setShowClient);
    const showClientMenu = useLayoutStore(state => state.showClientMenu);
    const setShowClientMenu = useLayoutStore(state => state.setShowClientMenu);
    const closeAll = useLayoutStore(state => state.closeMenu);

    const setShowLoading = useLayoutStore((state) => state.setShowLoading);

    const showInfo = useSnackbarStore((state) => state.show);

    const {del, loading} = useApiDelete();

    const query = useApiQuery();

    const onSave = async (newClient) => {
        await query.refetch();
        setShowCreate(false);
        setSelection(newClient);
    };

    const onClose = () => {
        setShowCreate(false);
    };

    const handleDelete = (client) => {
        deleteConfirm.show(client);
    };

    const deleteConfirm = useConfirmDialog(
        async (client) => {
            try {
                await del(client.id);
                query.refetch();
                showInfo(translate('client.action.delete_success'));
            } catch (e) {
                showInfo(e.message, {severity: 'warning'});
            }
        },
        undefined,
        translate('client.action.confirm_delete')
    );

    const handleEdit = (_id) => {
        setShowEdit(true);
    };

    const handleSelect = (client) => {
        setShowClient(true, client);
    };

    const closeMenu = () => {
        setShowClientMenu(false);
    };

    const toggleMenu = (e, client) => {
        if (e.dir === 'Left') {
            setSelection(client);
        } else if (e.dir === 'Right') {
            setSelection(null);
        } else {
            setSelection(selection === client ? null : client);
        }
    };

    const createHandler = () => {
        setShowCreate(true);
    };

    useEffect(() => {
        setShowLoading(loading || query.isFetching);
        return () => {
            setShowLoading(false);
        };
    }, [loading, query.isFetching, setShowLoading]);
    return (
        <Drawer open={showClientMenu} onClose={closeMenu}>
            <ConfirmDialog {...deleteConfirm} />
            <Create onSave={onSave} onClose={onClose} open={showCreate} />
            {showEdit && <Edit client={selection} onClose={() => setShowEdit(false)} onSave={onSave}/>}
            <MenuHeader
                text={<Text>{unTranslated('Vereine')}</Text>}
                handleBackButton={closeMenu}
                handleClose={closeAll}
            />

            <Stack sx={styles.container}>
                {hasAccess('clients')?.limit?.max > query.data.filter(cl => isOwner(cl.user_id)).length &&
                <Stack
                    sx={styles.actionButtonContainer} onClick={createHandler} direction="row" alignItems="center">
                    <Fab disableRipple sx={styles.actionButton}><AddIcon color="white"/></Fab>
                    <Typography><Text>client.action.create2</Text></Typography>
                </Stack>
                }
                {/* // TODO styling / translation  */}
                {hasAccess('clients')?.limit?.max <= query.data.filter(cl => isOwner(cl.user_id)).length && <Stack
                    sx={styles.actionButtonContainer} onClick={() => console.log('LINK TO PREMIUM PAGE')} direction="row" alignItems="center">
                    <Fab disableRipple sx={styles.actionButton}><AddIcon color="white"/></Fab>
                    <Typography><Text>board.packages.premium.clubs</Text></Typography>
                </Stack>}

                <List sx={styles.listHolder}>
                    {query.data?.map(client => <Client
                        key={client.id}
                        client={client}
                        onClick={handleSelect}
                        onToggleMenu={toggleMenu}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        showMenu={selection?.id === client.id}/>)}
                </List>
            </Stack>
        </Drawer>
    );
};

export default Menu;